/**
 * AB#22938 (Zendesk #10456) — cancelling an order before a factory is assigned.
 *
 * A confirmed order with no factory yet (status `order_approve` and no
 * `factory_id`, the "Pending for factory assignment" tab) used to have the whole
 * Move order picker disabled, so the admins had to assign a factory first just
 * to be able to cancel it. The backend's move does not need a factory to cancel,
 * so for such an item the picker stays open with only "Order Cancelled"
 * selectable; every other status still waits for a factory.
 */
import type { MoveOption } from '@/components/Dashboard/Order/components/MoveOrderPicker.vue';

export const ORDER_CANCEL = 'order_cancel';

/** Statuses an item can be cancelled from while it has no factory. */
export const CANCELLABLE_WITHOUT_FACTORY = [
  'order_approve',
  'pending_for_factory_assignment',
  'submitted_for_factory_review',
];

export const NO_FACTORY_HINT =
  'No factory assigned yet: the order can only be cancelled. Assign a factory to move it anywhere else.';

type ItemLike = { factory_id?: number | string | null; status?: string | null } | null | undefined;

export const canCancelWithoutFactory = (item: ItemLike): boolean =>
  !!item && !item.factory_id && CANCELLABLE_WITHOUT_FACTORY.includes(item.status ?? '');

/**
 * For an item without a factory, keep only "Order Cancelled" selectable.
 * Items that have a factory get their options back unchanged.
 */
export const lockUntilFactory = (options: MoveOption[], item: ItemLike): MoveOption[] => {
  if (!item || item.factory_id) return options;
  return options.map((o) =>
    o.value === ORDER_CANCEL ? o : { ...o, disabled: true, hint: NO_FACTORY_HINT, quiet: true }
  );
};
